import type { JSX, ParentProps } from 'solid-js';
import { PixelIcon } from './PixelIcon';
import { useWindowManager } from '../lib/windowManager';
import type { ManagedWindow, WindowBounds } from '../types/window';

interface WindowFrameProps extends ParentProps {
  window: ManagedWindow;
  active: boolean;
  onClose: () => void;
}

type ResizeDirection = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

export function WindowFrame(props: WindowFrameProps) {
  const manager = useWindowManager();

  const windowStyle = (): JSX.CSSProperties => {
    if (props.window.maximized) {
      return {
        left: '0px',
        top: '0px',
        width: '100%',
        height: '100%',
        'z-index': props.window.zIndex,
        display: props.window.minimized ? 'none' : 'flex',
      };
    }

    return {
      left: `${props.window.bounds.x}px`,
      top: `${props.window.bounds.y}px`,
      width: `${props.window.bounds.width}px`,
      height: `${props.window.bounds.height}px`,
      'z-index': props.window.zIndex,
      display: props.window.minimized ? 'none' : 'flex',
    };
  };

  function beginDrag(event: PointerEvent) {
    if (event.button !== 0 || props.window.maximized) return;
    const target = event.target as HTMLElement;
    if (target.closest('.title-bar-controls')) return;

    event.preventDefault();
    manager.focusWindow(props.window.id);
    const startX = event.clientX;
    const startY = event.clientY;
    const start = { ...props.window.bounds };

    const move = (moveEvent: PointerEvent) => {
      manager.setBounds(props.window.id, {
        ...start,
        x: start.x + moveEvent.clientX - startX,
        y: start.y + moveEvent.clientY - startY,
      });
    };

    const end = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end, { once: true });
    window.addEventListener('pointercancel', end, { once: true });
  }

  function beginResize(event: PointerEvent, direction: ResizeDirection) {
    if (event.button !== 0 || props.window.maximized || !props.window.resizable) return;
    event.preventDefault();
    event.stopPropagation();
    manager.focusWindow(props.window.id);

    const startX = event.clientX;
    const startY = event.clientY;
    const start = { ...props.window.bounds };

    const move = (moveEvent: PointerEvent) => {
      const dx = moveEvent.clientX - startX;
      const dy = moveEvent.clientY - startY;
      const next: WindowBounds = { ...start };

      if (direction.includes('e')) next.width = Math.max(props.window.minWidth, start.width + dx);
      if (direction.includes('s')) next.height = Math.max(props.window.minHeight, start.height + dy);
      if (direction.includes('w')) {
        const width = Math.max(props.window.minWidth, start.width - dx);
        next.x = start.x + (start.width - width);
        next.width = width;
      }
      if (direction.includes('n')) {
        const height = Math.max(props.window.minHeight, start.height - dy);
        next.y = start.y + (start.height - height);
        next.height = height;
      }

      manager.setBounds(props.window.id, next);
    };

    const end = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
    };

    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end, { once: true });
    window.addEventListener('pointercancel', end, { once: true });
  }

  return (
    <section
      class="window managed-window"
      classList={{ maximized: props.window.maximized, inactive: !props.active }}
      style={windowStyle()}
      onPointerDown={() => manager.focusWindow(props.window.id)}
      aria-label={props.window.title}
    >
      <div
        class="title-bar managed-title-bar"
        classList={{ inactive: !props.active }}
        onPointerDown={beginDrag}
        onDblClick={() => manager.toggleMaximize(props.window.id)}
      >
        <div class="title-bar-text managed-title-text">
          <PixelIcon name={props.window.icon} size={16} />
          <span>{props.window.title}</span>
        </div>
        <div class="title-bar-controls" onDblClick={(event) => event.stopPropagation()}>
          <button
            aria-label="Minimize"
            title="Minimize"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={() => manager.minimizeWindow(props.window.id)}
          />
          {props.window.resizable && (
            <button
              aria-label={props.window.maximized ? 'Restore' : 'Maximize'}
              title={props.window.maximized ? 'Restore' : 'Maximize'}
              onPointerDown={(event) => event.stopPropagation()}
              onClick={() => manager.toggleMaximize(props.window.id)}
            />
          )}
          <button
            aria-label="Close"
            title="Close"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={props.onClose}
          />
        </div>
      </div>
      <div class="managed-window-content">{props.children}</div>
      {props.window.resizable && !props.window.maximized && (
        <div class="resize-handles" aria-hidden="true">
          {(['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'] as ResizeDirection[]).map((direction) => (
            <div
              class={`resize-handle resize-${direction}`}
              onPointerDown={(event) => beginResize(event, direction)}
            />
          ))}
        </div>
      )}
    </section>
  );
}
